import React, { useState, useEffect } from "react";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../../../../core/data/redux/apis/urls";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocDandARCard } from "./common/NocDandARCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";
import { handleDocumentClick } from "./common/documentUtils";
import { DnarOption } from "./common/types";

interface NocPassportApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  dnarMasterList: DnarOption[];
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocPassportApproval: React.FC<NocPassportApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  dnarMasterList,
  onShowToast,
}) => {
  // D&AR State
  const [dnARStatus, setDnARStatus] = useState<string>("");
  const [dnARPenaltyEffective, setDnARPenaltyEffective] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");

  // Approver Remarks (HRemarks)
  const [hRemarks, setHRemarks] = useState<string>("");

  // Contact Number
  const [contactNumber, setContactNumber] = useState<string>("");
  const [contactNumberError, setContactNumberError] = useState<string>("");

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  const getFieldStatus = (fieldName: string): "EDITABLE" | "READONLY" | "HIDDEN" => {
    const rawVal =
      nocData?.enabled_fields_fields_json?.[fieldName] ||
      nocData?.enabled_fields_json?.[fieldName] ||
      nocData?.[fieldName];

    if (rawVal === "X" || rawVal === "V" || rawVal === true) return "EDITABLE";
    return "READONLY";
  };

  useEffect(() => {
    if (nocData) {
      setDnARStatus(nocData?.dnARStatus || nocData?.DnARStatus || nocData?.dandARStatus || "");
      setDnARPenaltyEffective(nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || nocData?.dandARPenaltyEffective || "");
      const isRemarksEditable = getFieldStatus("Remarks") === "EDITABLE";
      const initialRemarks = isRemarksEditable
        ? ""
        : ((nocData?.Remarks !== "X" && nocData?.Remarks) ||
           (nocData?.remarks !== "X" && nocData?.remarks) ||
           "");
      setRemarks(initialRemarks);
      setHRemarks("");
      setContactNumber(
        nocData?.contactNumber ||
        nocData?.employee?.phone ||
        nocData?.employee?.details?.phone ||
        ""
      );
    }
  }, [nocData]);

  const handleApproveReject = async (action: "A" | "R") => {
    const currentDnar = dnARStatus || nocData?.dnARStatus || "";

    // Validation for Contact Number if editable
    const isContactEditable =
      nocData?.enabled_fields_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_fields_json?.contactNumber === "X" ||
      nocData?.enabled_fields_json?.contactNumber === "X";

    if (isContactEditable) {
      if (!contactNumber || !/^\d{10}$/.test(contactNumber)) {
        setContactNumberError("Contact number must be exactly 10 digits");
        onShowToast("Error", "Contact number must be exactly 10 digits.", "danger");
        return;
      }
    }

    // Validation for D&AR Penalty Effective Date if M or J
    if ((currentDnar === "M" || currentDnar === "J") && !dnARPenaltyEffective) {
      onShowToast("Error", "Please select D&AR Penalty Effective Date.", "danger");
      return;
    }

    // Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const approverRemarks = (hRemarks || "").trim();
      const isRemarksEditable = getFieldStatus("Remarks") === "EDITABLE";
      const darRemarks = (isRemarksEditable ? remarks : (nocData?.Remarks || nocData?.remarks || remarks || "")).trim();

      const payload = {
        Approve_Reject_flag: action,
        ReferenceNumber: nocData.referenceNumber,
        HRemarks: approverRemarks,
        hRemarks: approverRemarks,
        Remarks1: approverRemarks,
        remarks1: approverRemarks,
        Remarks: darRemarks,
        remarks: darRemarks,
        DnARStatus: currentDnar,
        DnARPenaltyEffecti:
          currentDnar === "M" || currentDnar === "J"
            ? dnARPenaltyEffective || nocData?.dnARPenaltyEffecti || ""
            : "",
      };

      console.log("Passport Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc-visa-passport/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Passport/Visa NOC application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Passport Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const appliedForLabel = (() => {
    const val = String(nocData?.nOCAppliedFor || "").trim();
    if (val === "0") return "Fresh Passport";
    if (val === "1") return "Renewal of Passport";
    if (val === "2") return "Visa";
    return nocData?.nOCAppliedFor || "N/A";
  })();

  const passportTypeLabel = (() => {
    const pType = String(nocData?.passportType || "").toUpperCase().trim();
    if (pType === "O") return "Official";
    if (pType === "R") return "Regular";
    if (pType === "D") return "Diplomatic";
    return nocData?.passportType || "N/A";
  })();

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="NOC for Passport / Visa Approval"
          breadcrumbTitle="Passport/Visa Request Details"
          nocType="NOC_PASSPORT"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Info */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_PASSPORT"
          editableContactNumber={contactNumber}
          onContactNumberChange={(val) => {
            setContactNumber(val);
            if (/^\d{10}$/.test(val)) setContactNumberError("");
          }}
          contactNumberError={contactNumberError}
        />

        {/* Passport Details Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-id me-2 text-primary"></i>
              Passport / Visa Specific Information
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  NOC Applied For
                </label>
                <div className="fw-bold text-dark">{appliedForLabel}</div>
              </div>

              {(nocData?.nOCAppliedFor === "1" ||
                nocData?.nOCAppliedFor === "2" ||
                nocData?.passportNumber ||
                nocData?.passportNo) && (
                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Passport No.
                  </label>
                  <div className="fw-bold text-dark font-monospace">
                    {nocData?.passportNumber || nocData?.passportNo || "N/A"}
                  </div>
                </div>
              )}

              {(nocData?.nOCAppliedFor === "2" || nocData?.passportType) && (
                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Type of Passport
                  </label>
                  <div className="fw-bold text-dark">{passportTypeLabel}</div>
                </div>
              )}

              {(nocData?.nOCAppliedFor === "2" ||
                nocData?.placeOfVisitNOC ||
                nocData?.countriesToBeVisited ||
                nocData?.placeOfVisit) && (
                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Countries to be Visited
                  </label>
                  <div className="fw-bold text-dark">
                    {nocData?.placeOfVisitNOC ||
                      nocData?.countriesToBeVisited ||
                      nocData?.placeOfVisit ||
                      "N/A"}
                  </div>
                </div>
              )}

              {(nocData?.nOCAppliedFor === "2" || nocData?.purposeOfVisit) && (
                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Purpose of Visit
                  </label>
                  <div className="fw-bold text-dark">{nocData?.purposeOfVisit || "N/A"}</div>
                </div>
              )}

              {(nocData?.nOCAppliedFor === "2" ||
                nocData?.durationOfVisit ||
                nocData?.duration_of_visit) && (
                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Duration of Visit
                  </label>
                  <div className="fw-bold text-dark">
                    {nocData?.durationOfVisit || nocData?.duration_of_visit || "N/A"}
                  </div>
                </div>
              )}

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Pending Case / Court Proceedings
                </label>
                <div className="fw-bold text-dark">
                  {nocData?.pendingCase === "N" ? "No" : nocData?.pendingCase === "Y" ? "Yes" : nocData?.pendingCase || "No"}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether Any D&AR Pending
                </label>
                <div className="fw-bold text-dark">
                  {nocData?.dnARPending === "N" ? "No" : nocData?.dnARPending === "Y" ? "Yes" : nocData?.dnARPending || "No"}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether Any Vigilance Pending
                </label>
                <div className="fw-bold text-dark">
                  {nocData?.vigilancePending === "N" ? "No" : nocData?.vigilancePending === "Y" ? "Yes" : nocData?.vigilancePending || "No"}
                </div>
              </div>

              {/* Declaration Note */}
              <div className="col-12 mt-3">
                <div className="alert alert-info py-2 px-3 mb-2 small">
                  <i className="ti ti-info-circle me-1"></i>
                  <strong>Note:</strong> Employee should submit a good quality passport size photograph in HR department after final approval in ESS.
                </div>
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block uppercase fw-medium mb-2">Declaration Checklist</small>
                  <div className="d-flex align-items-center">
                    <input type="checkbox" className="form-check-input mt-0 me-2" checked disabled />
                    <span className="small text-dark">
                      I certify that I am not involved in any court/police/vigilance case and there is no restriction of court/any authority on foreign travel.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attachments */}
        {Array.isArray(nocData?.documents) && nocData.documents.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-paperclip me-2 text-primary"></i>
                Application Attachments
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {nocData.documents.map((doc: any, idx: number) => (
                  <button
                    key={doc.id || idx}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center"
                    onClick={() => handleDocumentClick(doc, "NOC_PASSPORT", onShowToast)}
                  >
                    <i className="ti ti-file me-1"></i>
                    {doc.fileName?.split("/").pop() || `Attachment ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Work History */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* D&AR Status */}
        <NocDandARCard
          nocData={nocData}
          nocType="NOC_PASSPORT"
          dnARStatus={dnARStatus}
          dnARPenaltyEffective={dnARPenaltyEffective}
          remarks={remarks}
          dnarMasterList={dnarMasterList}
          onStatusChange={setDnARStatus}
          onPenaltyEffectiveChange={setDnARPenaltyEffective}
          onRemarksChange={setRemarks}
          getFieldStatus={getFieldStatus}
        />

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_PASSPORT"
          onShowToast={onShowToast}
        />

        {/* Actions Modal */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
